import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { FeedbackItem } from '../types/taActionBoard';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

/** The fields of a Feedback card the model writes — what `POST /model-cards` fills in. */
export type ModelCardFields = Pick<
  FeedbackItem,
  'consensus' | 'decision' | 'summary' | 'bullets' | 'archiveReason' | 'emailDraft'
>;

export type ModelCardResult =
  | { state: 'ready'; fields: ModelCardFields }
  /** Still being written — ask again. */
  | { state: 'pending' }
  /** Lever could not say whose file this is, so the proxy did not open it — ask again. */
  | { state: 'unreadable' }
  /** Nothing queued and nothing cached: the card keeps the interviewer's rating. */
  | { state: 'gone' };

/**
 * The model output for Feedback cards the board shipped with `modelPending`.
 *
 * The proxy WAITS for generations still running (up to ~45 s), so this is one long request rather
 * than a polling loop — see proxy-server/lib/modelCards.js.
 */
export async function fetchModelCards(
  items: Array<Pick<FeedbackItem, 'opportunityId' | 'ratingLabel'>>
): Promise<Record<string, ModelCardResult>> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/ta-action-board/model-cards`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: items.map((i) => ({ opportunityId: i.opportunityId, ratingLabel: i.ratingLabel ?? null })),
    }),
    signal: AbortSignal.timeout(60_000),
    // 503 = the proxy could not confirm who is asking (it fails closed). Transient, and the cards
    // simply stay "being written" until the next ask — not worth an error toast.
    suppressErrorStatuses: [503],
  });
  if (!res.ok) throw new Error(`model-cards ${res.status}`);
  const data = (await res.json()) as { cards?: Record<string, ModelCardResult> };
  return data.cards || {};
}
